import React, { useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  TOAST_POSITIONS,
  type Toast as ToastModel,
  type ToastPosition,
} from "../store/toast-store";
import { useDefaultToastPosition, useVisibleToasts } from "../hooks/use-toast";
import { Toast } from "./Toast";

/**
 * Mobilde toast'lar ekran genişliğini kaplar, sm ve üzerinde köşeye sabitlenir.
 * Alt pozisyonlarda cihaz güvenli alanı (home indicator) hesaba katılır.
 */
const POSITION_CLASSES: Record<ToastPosition, string> = {
  "top-left": "top-2 left-2 right-2 flex-col-reverse sm:right-auto sm:top-4 sm:left-4",
  "top-center":
    "top-2 left-2 right-2 flex-col-reverse sm:left-1/2 sm:right-auto sm:top-4 sm:-translate-x-1/2",
  "top-right": "top-2 left-2 right-2 flex-col-reverse sm:left-auto sm:top-4 sm:right-4",
  "bottom-left":
    "bottom-2 left-2 right-2 flex-col pb-[env(safe-area-inset-bottom)] sm:right-auto sm:bottom-4 sm:left-4",
  "bottom-center":
    "bottom-2 left-2 right-2 flex-col pb-[env(safe-area-inset-bottom)] sm:left-1/2 sm:right-auto sm:bottom-4 sm:-translate-x-1/2",
  "bottom-right":
    "bottom-2 left-2 right-2 flex-col pb-[env(safe-area-inset-bottom)] sm:left-auto sm:bottom-4 sm:right-4",
};

export const ToastContainer: React.FC = () => {
  const { t } = useTranslation();
  const toasts = useVisibleToasts();
  const defaultPosition = useDefaultToastPosition();

  const toastsByPosition = useMemo(() => {
    const grouped = {} as Record<ToastPosition, ToastModel[]>;

    for (const toast of toasts) {
      const position = toast.position ?? defaultPosition;
      (grouped[position] ??= []).push(toast);
    }

    return grouped;
  }, [toasts, defaultPosition]);

  // Altı pozisyon kabı her zaman mount edilir. Bunun iki faydası var: son toast
  // kapanırken çıkış animasyonu oynayabilir, ve ekran okuyucular önceden var
  // olan live region'lara eklenen içeriği daha güvenilir seslendirir.
  return (
    <>
      {TOAST_POSITIONS.map((position) => (
        <ol
          key={position}
          aria-live="polite"
          aria-atomic="false"
          aria-relevant="additions text"
          aria-label={t("toast.regionLabel")}
          // Destek ekran görüntüsü yakalarken bildirimler kadraja girmemeli
          data-screenshot-exclude=""
          className={cn(
            // Medya görüntüleyiciler z-[99999] kullandığı için üstünde kalmalı
            "pointer-events-none fixed z-[100000] flex w-auto gap-2 sm:w-[380px]",
            POSITION_CLASSES[position]
          )}
        >
          <AnimatePresence initial={false} mode="popLayout">
            {toastsByPosition[position]?.map((toast) => (
              <Toast key={toast.id} toast={toast} />
            ))}
          </AnimatePresence>
        </ol>
      ))}
    </>
  );
};
